import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TrackingPanel = ({ 
  selectedOrder, 
  onStatusUpdate, 
  onDeliveryConfirmation, 
  onSendNotification,
  onClose 
}) => {
  const [deliveryForm, setDeliveryForm] = useState({
    signedBy: '',
    notes: '',
    photo: null
  });
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);

  if (!selectedOrder) {
    return (
      <div className="bg-surface rounded-lg border border-border p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-body-medium text-text-primary mb-2">
            Selecciona un pedido
          </h3>
          <p className="text-text-muted">
            Haz clic en un pedido de la lista para ver su información de seguimiento
          </p>
        </div>
      </div>
    );
  }

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    const icons = {
      processing: 'Clock',
      packed: 'Package',
      shipped: 'Send',
      in_transit: 'Truck',
      delivered: 'CheckCircle'
    };
    return icons[status] || 'Clock';
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: 'text-info',
      packed: 'text-warning',
      shipped: 'text-primary',
      in_transit: 'text-secondary',
      delivered: 'text-success'
    };
    return colors[status] || 'text-info';
  };

  const handleDeliverySubmit = (e) => {
    e.preventDefault();
    onDeliveryConfirmation(selectedOrder.id, deliveryForm.signedBy, deliveryForm.photo);
    setDeliveryForm({ signedBy: '', notes: '', photo: null });
    setShowDeliveryForm(false);
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setDeliveryForm(prev => ({ ...prev, photo: file }));
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading-semibold text-text-primary">
            Seguimiento del Pedido
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Order Info */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-body-medium text-text-primary">
              Información del Pedido
            </h3>
            <span className="text-sm font-data text-text-secondary">
              {selectedOrder.id}
            </span>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-text-muted">Cliente:</span>
              <span className="text-sm text-text-primary">{selectedOrder.customerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-text-muted">Fecha del pedido:</span>
              <span className="text-sm text-text-primary">
                {formatDateTime(selectedOrder.orderDate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-text-muted">Importe total:</span>
              <span className="text-sm font-body-medium text-text-primary">
                {new Intl.NumberFormat('es-ES', {
                  style: 'currency',
                  currency: 'EUR'
                }).format(selectedOrder.totalAmount)}
              </span>
            </div>
            {selectedOrder.trackingNumber && (
              <div className="flex justify-between">
                <span className="text-sm text-text-muted">Nº seguimiento:</span>
                <span className="text-sm font-data text-text-primary">
                  {selectedOrder.trackingNumber}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Products */}
        <div>
          <h3 className="text-sm font-body-medium text-text-primary mb-3">
            Productos ({selectedOrder.products?.length})
          </h3>
          <div className="space-y-2">
            {selectedOrder.products?.map((product, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-secondary-50 rounded-lg">
                <div>
                  <div className="text-sm font-body-medium text-text-primary">
                    {product.name}
                  </div>
                  <div className="text-xs text-text-muted">
                    SKU: {product.sku}
                  </div>
                </div>
                <div className="text-sm text-text-primary">
                  x{product.quantity}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Address */}
        <div>
          <h3 className="text-sm font-body-medium text-text-primary mb-3">
            Dirección de Entrega
          </h3>
          <div className="p-3 bg-secondary-50 rounded-lg">
            <div className="text-sm text-text-primary">
              {selectedOrder.deliveryAddress?.street}
            </div>
            <div className="text-sm text-text-primary">
              {selectedOrder.deliveryAddress?.postalCode} {selectedOrder.deliveryAddress?.city}
            </div>
            <div className="text-sm text-text-muted">
              {selectedOrder.deliveryAddress?.country}
            </div>
          </div>
        </div>

        {/* Status History */}
        <div>
          <h3 className="text-sm font-body-medium text-text-primary mb-3">
            Historial de Estados
          </h3>
          <div className="space-y-3">
            {selectedOrder.statusHistory?.map((entry, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className={`
                  flex items-center justify-center w-8 h-8 rounded-full 
                  ${entry.status === selectedOrder.status ? 'bg-primary' : 'bg-secondary-200'}
                `}>
                  <Icon 
                    name={getStatusIcon(entry.status)} 
                    size={14} 
                    className={entry.status === selectedOrder.status ? 'text-white' : 'text-text-muted'}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-body-medium ${
                      entry.status === selectedOrder.status ? 'text-primary' : 'text-text-primary'
                    }`}>
                      {entry.note}
                    </span>
                  </div>
                  <div className="text-xs text-text-muted">
                    {formatDateTime(entry.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Confirmation */}
        {selectedOrder.status === 'in_transit' && !showDeliveryForm && (
          <div className="pt-4 border-t border-border">
            <Button
              onClick={() => setShowDeliveryForm(true)}
              className="w-full"
              iconName="CheckCircle"
            >
              Confirmar Entrega
            </Button>
          </div>
        )}

        {showDeliveryForm && (
          <div className="pt-4 border-t border-border">
            <h3 className="text-sm font-body-medium text-text-primary mb-3">
              Confirmar Entrega
            </h3>
            <form onSubmit={handleDeliverySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Firmado por
                </label>
                <Input
                  type="text"
                  value={deliveryForm.signedBy}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, signedBy: e.target.value }))}
                  placeholder="Nombre de quien recibe"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Notas adicionales
                </label>
                <textarea
                  value={deliveryForm.notes}
                  onChange={(e) => setDeliveryForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Observaciones de la entrega..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Foto de entrega
                </label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary hover:file:bg-primary-100"
                />
              </div>

              <div className="flex space-x-3">
                <Button type="submit" className="flex-1">
                  Confirmar Entrega
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowDeliveryForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Actions */}
        <div className="pt-4 border-t border-border">
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => onSendNotification(selectedOrder.id, 'status_update')}
              iconName="Mail"
              size="sm"
            >
              Notificar
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`https://google.com/search?q=${selectedOrder.trackingNumber}`, '_blank')}
              iconName="MapPin"
              size="sm"
              disabled={!selectedOrder.trackingNumber}
            >
              Rastrear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingPanel;